import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ar } from "date-fns/locale/ar";
import "./AddVenue.css";
import CurrentStep from "./CurrentStep";
import Loading from "../../components/Loader";
import Success from "./Success";
import AuthContext from "../../contexts/AuthProvider";
import { API_URL } from "../../utils/config";

// Register Arabic locale for react-datepicker
registerLocale("ar", ar);

const baseUrl = API_URL;

const StepThreeAvailability = ({ update, status, setStatus }) => {
  const { t } = useTranslation("addvenue");
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useContext(AuthContext);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [unavailableDates, setUnavailableDates] = useState(new Map()); // Map of month-year keys to Set of days
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [venueId, setVenueId] = useState(null);
  const [workingHours, setWorkingHours] = useState(null); // Store fetched working hours for edit mode
  const [originalBlockedDays, setOriginalBlockedDays] = useState([]); // Store original blocked days from server
  const [errorMessage, setErrorMessage] = useState(""); // Store error message for display
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  const currentLang = getLangFromLocalStorage();

  // Localized day names
  const dayNames =
    currentLang === "ar"
      ? [
          "الاثنين",
          "الثلاثاء",
          "الأربعاء",
          "الخميس",
          "الجمعة",
          "السبت",
          "الأحد",
        ]
      : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  // Helper function to convert numbers to Arabic numerals if needed
  const formatDayNumber = (day) => {
    if (currentLang === "ar") {
      // Convert to Arabic-Indic numerals (Eastern Arabic numerals)
      const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
      return String(day).replace(
        /\d/g,
        (digit) => arabicNumerals[parseInt(digit)]
      );
    }
    return String(day).padStart(2, "0");
  };

  // Helper functions for working hours conversion (similar to StepTwo)
  const convertToLocal = (utcTime) => {
    const [hours, minutes] = utcTime.split(":").map(Number);
    const utcDate = new Date();
    utcDate.setUTCHours(hours, minutes, 0, 0); // Set UTC time

    // Get local time
    const localHours = utcDate.getHours();
    const localMinutes = utcDate.getMinutes();

    return `${localHours.toString().padStart(2, "0")}:${localMinutes
      .toString()
      .padStart(2, "0")}`;
  };

  const mapWorkingDaysToWorkingHours = (workingDays) => {
    // Initialize the workingHours object with default values for all days
    const workingHours = {
      Saturday: { enabled: false, from: "9:00", to: "17:00" },
      Sunday: { enabled: false, from: "9:00", to: "17:00" },
      Monday: { enabled: false, from: "9:00", to: "17:00" },
      Tuesday: { enabled: false, from: "9:00", to: "17:00" },
      Wednesday: { enabled: false, from: "9:00", to: "17:00" },
      Thursday: { enabled: false, from: "9:00", to: "17:00" },
      Friday: { enabled: false, from: "9:00", to: "17:00" },
    };

    // Helper function to convert day number to day name
    const getDayName = (dayNumber) => {
      const days = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      return days[dayNumber];
    };

    // Update workingHours with data from workingDays
    if (workingDays && Array.isArray(workingDays)) {
      workingDays.forEach((day) => {
        const dayName = getDayName(day.day); // Convert day number to day name
        workingHours[dayName] = {
          enabled: true, // Mark the day as enabled
          from: convertToLocal(day.start_time), // Set the 'from' time
          to: convertToLocal(day.end_time), // Set the 'to' time
        };
      });
    }

    return workingHours;
  };

  // Helper functions for month-specific date management
  const getMonthKey = (date) => {
    return `${date.getFullYear()}-${date.getMonth()}`;
  };

  const getCurrentMonthUnavailableDates = () => {
    const monthKey = getMonthKey(currentDate);
    return unavailableDates.get(monthKey) || new Set();
  };

  const setCurrentMonthUnavailableDates = (dates) => {
    const monthKey = getMonthKey(currentDate);
    const newUnavailableDates = new Map(unavailableDates);
    if (dates.size === 0) {
      newUnavailableDates.delete(monthKey);
    } else {
      newUnavailableDates.set(monthKey, dates);
    }
    setUnavailableDates(newUnavailableDates);

    // Save to localStorage for non-edit mode in YYYY-MM-DD format
    const searchParams = new URLSearchParams(location.search);
    const isEditMode = searchParams.get("edit") === "true";
    if (!isEditMode) {
      // Convert Map with Set values to array of YYYY-MM-DD formatted strings
      const dateStrings = [];
      newUnavailableDates.forEach((daysSet, monthKey) => {
        const [year, month] = monthKey.split("-").map(Number);
        daysSet.forEach((day) => {
          // Create date string directly to avoid timezone issues
          const paddedMonth = (month + 1).toString().padStart(2, "0"); // Convert from 0-indexed to 1-indexed
          const paddedDay = day.toString().padStart(2, "0");
          const dateStr = `${year}-${paddedMonth}-${paddedDay}`;
          dateStrings.push(dateStr);
        });
      });
      localStorage.setItem("unavailableDates", JSON.stringify(dateStrings));
    }
  };

  // Get venue ID from URL params or localStorage
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get("id") || localStorage.getItem("venueId");
    setVenueId(id);

    const isEditMode = searchParams.get("edit") === "true";

    if (isEditMode && id) {
      // Load existing unavailable dates from server if editing
      fetchUnavailableDates(id);
    } else {
      // Load unavailable dates from localStorage if not editing
      const savedDates = localStorage.getItem("unavailableDates");
      if (savedDates) {
        try {
          const dateStrings = JSON.parse(savedDates);
          const dateMap = new Map();

          // Handle both old format (array of arrays) and new format (array of strings)
          if (Array.isArray(dateStrings) && dateStrings.length > 0) {
            if (Array.isArray(dateStrings[0])) {
              // Old format: [["2025-10", [1,4,7,8]], ...]
              dateStrings.forEach(([key, valueArray]) => {
                dateMap.set(key, new Set(valueArray));
              });
            } else {
              // New format: ["2025-10-01", "2025-10-04", ...]
              dateStrings.forEach((dateStr) => {
                const date = new Date(dateStr);
                const monthKey = getMonthKey(date);
                const day = date.getDate();

                if (!dateMap.has(monthKey)) {
                  dateMap.set(monthKey, new Set());
                }
                dateMap.get(monthKey).add(day);
              });
            }
          }

          setUnavailableDates(dateMap);
        } catch (error) {
          console.error("Error parsing saved unavailable dates:", error);
        }
      }
    }
  }, [location]);

  // Note: Removed the automatic addition of working hours to unavailable dates
  // Working hours unavailability is now handled only in display logic

  const fetchUnavailableDates = async (id) => {
    try {
      if (!isAuthenticated) return;

      const response = await fetch(
        `${baseUrl}/api/v1/attnd-venue/venues/${id}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const venue = data.data || data;

        // Process working hours data
        if (venue.working_days) {
          const fetchedWorkingHours = mapWorkingDaysToWorkingHours(
            venue.working_days
          );
          setWorkingHours(fetchedWorkingHours);
        }

        // Process unavailable dates - stored as objects with date property
        if (venue.blocked_days && Array.isArray(venue.blocked_days)) {
          // Extract date strings for comparison and store original blocked days
          const originalDateStrings = venue.blocked_days.map((item) =>
            typeof item === "string" ? item : item.date
          );
          setOriginalBlockedDays([...originalDateStrings]);

          const unavailableMap = new Map();

          venue.blocked_days.forEach((item) => {
            try {
              // Handle both string format and object format
              const dateStr = typeof item === "string" ? item : item.date;
              const date = new Date(dateStr);
              const monthKey = getMonthKey(date);
              const day = date.getDate();

              if (!unavailableMap.has(monthKey)) {
                unavailableMap.set(monthKey, new Set());
              }
              unavailableMap.get(monthKey).add(day);
            } catch (error) {
              console.error("Error parsing date:", item, error);
            }
          });

          setUnavailableDates(unavailableMap);
        }
      }
    } catch (error) {
      console.error("Error fetching unavailable dates:", error);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1;
  };

  // Helper function to check if a day is unavailable due to working hours (for current month)
  const isDayUnavailableDueToWorkingHours = (day) => {
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    return isDayUnavailableDueToWorkingHoursForDate(date, day);
  };

  // Helper function to check if any date is unavailable due to working hours
  const isDayUnavailableDueToWorkingHoursForDate = (date, day) => {
    try {
      const searchParams = new URLSearchParams(location.search);
      const isEditMode = searchParams.get("edit") === "true";

      let workingHoursToCheck = null;

      if (isEditMode && workingHours) {
        // Use fetched working hours in edit mode
        workingHoursToCheck = workingHours;
      } else {
        // Use localStorage working hours in non-edit mode
        const savedWorkingHours = localStorage.getItem("workingHours");
        if (!savedWorkingHours) return false;
        workingHoursToCheck = JSON.parse(savedWorkingHours);
      }

      if (!workingHoursToCheck) return false;

      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.

      const getDayNumber = (dayName) => {
        const days = {
          Sunday: 0,
          Monday: 1,
          Tuesday: 2,
          Wednesday: 3,
          Thursday: 4,
          Friday: 5,
          Saturday: 6,
        };
        return days[dayName];
      };

      const dayName = Object.keys(workingHoursToCheck).find(
        (name) => getDayNumber(name) === dayOfWeek
      );

      return dayName && !workingHoursToCheck[dayName]?.enabled;
    } catch (error) {
      console.error("Error checking working hours:", error);
      return false;
    }
  };

  // Helper function to check if a day is in the past
  const isDayInPast = (day) => {
    const today = new Date();
    const selectedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    return selectedDate < todayDate;
  };

  const toggleDate = (day) => {
    // Check if this day is in the past
    if (isDayInPast(day)) {
      // Don't allow toggling past dates
      return;
    }

    // Check if this day is unavailable due to working hours
    if (isDayUnavailableDueToWorkingHours(day)) {
      // Don't allow toggling days that are unavailable due to working hours
      return;
    }

    const currentMonthDates = getCurrentMonthUnavailableDates();
    const newCurrentMonthDates = new Set(currentMonthDates);

    if (newCurrentMonthDates.has(day)) {
      newCurrentMonthDates.delete(day);
    } else {
      newCurrentMonthDates.add(day);
    }

    setCurrentMonthUnavailableDates(newCurrentMonthDates);
  };

  const handleSave = async (isEditMode = false) => {
    if (!isEditMode) {
      // If not in edit mode, just save to localStorage in YYYY-MM-DD format
      // Filter out working-hours-based unavailable days, only save manually blocked ones
      const dateStrings = [];
      unavailableDates.forEach((daysSet, monthKey) => {
        const [year, month] = monthKey.split("-").map(Number);
        daysSet.forEach((day) => {
          // Create date string directly to avoid timezone issues
          const paddedMonth = (month + 1).toString().padStart(2, "0"); // Convert from 0-indexed to 1-indexed
          const paddedDay = day.toString().padStart(2, "0");
          const dateStr = `${year}-${paddedMonth}-${paddedDay}`;

          // Check if this day is unavailable due to working hours
          const tempDate = new Date(year, month, day);

          // Only save manually blocked days, not working-hours-based ones
          if (!isDayUnavailableDueToWorkingHoursForDate(tempDate, day)) {
            dateStrings.push(dateStr);
          }
        });
      });
      localStorage.setItem("unavailableDates", JSON.stringify(dateStrings));
      return;
    }

    if (!venueId) {
      console.error("No venue ID available");
      return;
    }

    // Convert current unavailable dates map to array of full date strings
    // Filter out working-hours-based unavailable days, only include manually blocked ones
    const currentUnavailableDatesArray = [];

    unavailableDates.forEach((daysSet, monthKey) => {
      const [year, month] = monthKey.split("-").map(Number);
      daysSet.forEach((day) => {
        // Create date string directly to avoid timezone issues
        const paddedMonth = (month + 1).toString().padStart(2, "0"); // Convert from 0-indexed to 1-indexed
        const paddedDay = day.toString().padStart(2, "0");
        const dateStr = `${year}-${paddedMonth}-${paddedDay}`;

        // Create date for working hours check
        const date = new Date(year, month, day);

        // Only include manually blocked days, not working-hours-based ones
        if (!isDayUnavailableDueToWorkingHoursForDate(date, day)) {
          currentUnavailableDatesArray.push(dateStr);
        }
      });
    });

    // Calculate newly blocked days (current unavailable - original blocked)
    const newlyBlockedDays = currentUnavailableDatesArray.filter(
      (dateStr) => !originalBlockedDays.includes(dateStr)
    );

    // Calculate newly unblocked days (original blocked - current unavailable)
    const newlyUnblockedDays = originalBlockedDays.filter(
      (dateStr) => !currentUnavailableDatesArray.includes(dateStr)
    );

    // Only make API call if there are changes
    if (newlyBlockedDays.length === 0 && newlyUnblockedDays.length === 0) {
      console.log("No changes to save");
      setStatus("Success");
      return;
    }

    try {
      setStatus("loading");
      setErrorMessage(""); // Clear any previous error message

      const response = await fetch(
        `${baseUrl}/api/v1/attnd-venue/calendar/toggleBlockedDate`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
          body: JSON.stringify({
            venue_id: venueId,
            blocked_days: newlyBlockedDays, // Send as array of date strings (YYYY-MM-DD)
            unblocked_days: newlyUnblockedDays, // Send as array of date strings (YYYY-MM-DD)
          }),
        }
      );

      if (response.ok) {
        setStatus("Success");
        setErrorMessage(""); // Clear any previous error message
        // Update original blocked days to current state for future comparisons
        setOriginalBlockedDays([...currentUnavailableDatesArray]);
        console.log("Availability updated successfully");
      } else {
        // Parse error response
        const errorData = await response.json();
        const errorMessage = errorData.error || errorData.message || `Failed to update availability: ${response.statusText}`;
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error saving availability:", error);
      setErrorMessage(error.message || "An error occurred while saving availability");
      setStatus("error");
    }
  };

  const handleNavigateWithParams = (step) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("step", step);
    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  const handleNavigateWithEdit = (step) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("step", step);
    searchParams.set("edit", "true");
    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };
  useEffect(() => {
    if (status === "Success") {
      // Set a timeout to reset the status after 3 seconds
      const timer = setTimeout(() => {
        setStatus(""); // Reset status to empty string
      }, 2000); // 2000 milliseconds = 2 seconds

      // Cleanup the timer if the component unmounts or status changes
      return () => clearTimeout(timer);
    }
  }, [status]);

  useEffect(() => {
    if (status === "error") {
      // Set a timeout to reset the error status after 5 seconds
      const timer = setTimeout(() => {
        setStatus(""); // Reset status to empty string
        setErrorMessage(""); // Clear error message
      }, 5000); // 5000 milliseconds = 5 seconds

      // Cleanup the timer if the component unmounts or status changes
      return () => clearTimeout(timer);
    }
  }, [status]);

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDayOffset = getFirstDayOfMonth(currentDate);

  const weeks = [];
  let currentWeek = Array(firstDayOffset).fill(null);

  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  const searchParams = new URLSearchParams(location.search);
  const isEdit = searchParams.get("edit") === "true";
  const currentMonthUnavailableDates = getCurrentMonthUnavailableDates();

  return (
    <div className="steps" style={isEdit ? { margin: "0" } : {}}>
      <CurrentStep step={3} />
      <div className="Dates">
        {/* Month Status Display */}
        {/* <div className="month-status">
          <span className="total-unavailable-days">
            {t("stepThreeAvailability.totalDays") || "Total unavailable days"}: {Array.from(unavailableDates.values()).reduce((total, daysSet) => total + daysSet.size, 0)}
          </span>
          {currentMonthUnavailableDates.size > 0 && (
            <span className="current-month-unavailable">
              {t("stepThreeAvailability.thisMonth") || "This month"}: {currentMonthUnavailableDates.size} day{currentMonthUnavailableDates.size !== 1 ? 's' : ''}
            </span>
          )}
        </div> */}

        <div
          className="availability-calendar"
          dir={currentLang === "ar" ? "rtl" : "ltr"}
        >
          {/* Month Navigation */}
          <div className="calendar-header">
            <div className="calendar-title">
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.7727 8.45739L23.5433 12.2294M24.298 5.44139L26.5607 7.70406C26.7606 7.90407 26.8728 8.17528 26.8728 8.45806C26.8728 8.74083 26.7606 9.01204 26.5607 9.21206L11.286 24.4854L6.57267 25.4281L7.51533 20.7147L22.7887 5.44006C22.8877 5.34088 23.0054 5.26221 23.1349 5.20853C23.2644 5.15485 23.4032 5.12722 23.5433 5.12722C23.6835 5.12722 23.8223 5.15485 23.9518 5.20853C24.0813 5.26221 24.1989 5.34221 24.298 5.44139ZM6.40867 3.70139L5.906 5.71206C5.89422 5.75873 5.87004 5.80135 5.836 5.83539C5.80196 5.86942 5.75934 5.89361 5.71267 5.90539L3.702 6.40806C3.43267 6.47472 3.43267 6.85872 3.702 6.92539L5.71267 7.42806C5.75934 7.43983 5.80196 7.46402 5.836 7.49806C5.87004 7.5321 5.89422 7.57472 5.906 7.62139L6.40867 9.63206C6.47533 9.90139 6.85933 9.90139 6.926 9.63206L7.42867 7.62139C7.44044 7.57472 7.46463 7.5321 7.49867 7.49806C7.53271 7.46402 7.57533 7.43983 7.622 7.42806L9.63267 6.92539C9.902 6.85872 9.902 6.47472 9.63267 6.40806L7.622 5.90539C7.57533 5.89361 7.53271 5.86942 7.49867 5.83539C7.46463 5.80135 7.44044 5.75873 7.42867 5.71206L6.926 3.70139C6.85933 3.43206 6.47533 3.43206 6.40867 3.70139ZM25.334 22.6667L24.8007 24.8001L22.6673 25.3334L24.8007 25.8667L25.334 28.0001L25.8673 25.8667L28.0007 25.3334L25.8673 24.8001L25.334 22.6667Z"
                  stroke="black"
                  stroke-width="2"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <h4>
                {t("availabilitySubtitle") ||
                  "Mark the days you're unavailable to take bookings"}
              </h4>
            </div>

            <div className="month-year-selector">
              <div className="month-year-datepicker-container">
                <div className="month-year-selector-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 5.75C7.59 5.75 7.25 5.41 7.25 5V2C7.25 1.59 7.59 1.25 8 1.25C8.41 1.25 8.75 1.59 8.75 2V5C8.75 5.41 8.41 5.75 8 5.75Z"
                      fill="#F9F9F9"
                    />
                    <path
                      d="M16 5.75C15.59 5.75 15.25 5.41 15.25 5V2C15.25 1.59 15.59 1.25 16 1.25C16.41 1.25 16.75 1.59 16.75 2V5C16.75 5.41 16.41 5.75 16 5.75Z"
                      fill="#F9F9F9"
                    />
                    <path
                      d="M15 22.75H9C3.38 22.75 2.25 20.1 2.25 15.82V9.65C2.25 4.91 3.85 2.98 7.96 2.75H16C16.01 2.75 16.03 2.75 16.04 2.75C20.15 2.98 21.75 4.91 21.75 9.65V15.82C21.75 20.1 20.62 22.75 15 22.75ZM8 4.25C5.2 4.41 3.75 5.29 3.75 9.65V15.82C3.75 19.65 4.48 21.25 9 21.25H15C19.52 21.25 20.25 19.65 20.25 15.82V9.65C20.25 5.3 18.81 4.41 15.98 4.25H8Z"
                      fill="#F9F9F9"
                    />
                    <path
                      d="M20.75 18.35H3.25C2.84 18.35 2.5 18.01 2.5 17.6C2.5 17.19 2.84 16.85 3.25 16.85H20.75C21.16 16.85 21.5 17.19 21.5 17.6C21.5 18.01 21.16 18.35 20.75 18.35Z"
                      fill="#F9F9F9"
                    />
                    <path
                      d="M12 8.25C10.77 8.25 9.73 8.92 9.73 10.22C9.73 10.84 10.02 11.31 10.46 11.61C9.85 11.97 9.5 12.55 9.5 13.23C9.5 14.47 10.45 15.24 12 15.24C13.54 15.24 14.5 14.47 14.5 13.23C14.5 12.55 14.15 11.96 13.53 11.61C13.98 11.3 14.26 10.84 14.26 10.22C14.26 8.92 13.23 8.25 12 8.25ZM12 11.09C11.48 11.09 11.1 10.78 11.1 10.29C11.1 9.79 11.48 9.5 12 9.5C12.52 9.5 12.9 9.79 12.9 10.29C12.9 10.78 12.52 11.09 12 11.09ZM12 14C11.34 14 10.86 13.67 10.86 13.07C10.86 12.47 11.34 12.15 12 12.15C12.66 12.15 13.14 12.48 13.14 13.07C13.14 13.67 12.66 14 12 14Z"
                      fill="#F9F9F9"
                    />
                  </svg>
                </div>
                <DatePicker
                  selected={currentDate}
                  onChange={(date) => {
                    if (date) {
                      setCurrentDate(date);
                    }
                  }}
                  showMonthYearPicker
                  dateFormat="MMMM, yyyy"
                  className="month-year-datepicker"
                  calendarClassName="month-year-datepicker-calendar"
                  yearDropdownItemNumber={7}
                  scrollableYearDropdown
                  minDate={new Date(new Date().getFullYear(), 0, 1)}
                  locale={currentLang === "ar" ? "ar" : undefined}
                />
                <div className="month-year-dropdown-icon">
                  <svg
                    width="24"
                    height="12"
                    viewBox="0 0 24 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_3605_60547)">
                      <path
                        d="M17.4198 2.45199L18.4798 3.51299L12.7028 9.29199C12.6102 9.38514 12.5001 9.45907 12.3789 9.50952C12.2576 9.55997 12.1276 9.58594 11.9963 9.58594C11.8649 9.58594 11.7349 9.55997 11.6137 9.50952C11.4924 9.45907 11.3823 9.38514 11.2898 9.29199L5.50977 3.51299L6.56977 2.45299L11.9948 7.87699L17.4198 2.45199Z"
                        fill="#F9F9F9"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3605_60547">
                        <rect
                          width="12"
                          height="24"
                          fill="white"
                          transform="matrix(0 1 -1 0 24 0)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="calendar-grid">
            {/* Calendar Days */}
            <div className="calendar-weeks">
              {weeks.map((week, weekIdx) => (
                <div key={weekIdx} className="calendar-week">
                  {week.map((day, dayIdx) => {
                    const dayName = dayNames[dayIdx];
                    return (
                      <button
                        key={dayIdx}
                        onClick={() => day && toggleDate(day)}
                        disabled={!day || (day && isDayInPast(day))}
                        className={`calendar-day ${
                          !day ? "calendar-day-empty" : ""
                        } ${
                          day && isDayInPast(day)
                            ? "calendar-day-past"
                            : day && isDayUnavailableDueToWorkingHours(day)
                            ? "calendar-day-working-hours-unavailable"
                            : day && currentMonthUnavailableDates.has(day)
                            ? "calendar-day-unavailable"
                            : "calendar-day-available"
                        }`}
                        title={
                          day && isDayInPast(day)
                            ? t("pastDate") || "Past Date (Not Available)"
                            : day && isDayUnavailableDueToWorkingHours(day)
                            ? t("workingHoursUnavailable") ||
                              "Unavailable (Working Hours)"
                            : day && currentMonthUnavailableDates.has(day)
                            ? t("manuallyUnavailable") || "Manually Unavailable"
                            : t("available") || "Available"
                        }
                      >
                        {day && (
                          <>
                            <span className="calendar-day-name">{dayName}</span>
                            <span className="calendar-day-number">
                              {formatDayNumber(day)}
                            </span>
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Calendar Legend */}
          <div className="calendar-legend">
            <div className="legend-item">
              <div className="legend-color legend-available"></div>
              <span>{t("availableDay") || "Available"}</span>
            </div>
            <div className="legend-item">
              <div className="legend-color legend-unavailable"></div>
              <span>{t("manuallyUnavailable") || "Manually Unavailable"}</span>
            </div>
            <div className="legend-item">
              <div className="legend-color legend-working-hours-unavailable"></div>
              <span>
                {t("workingHoursUnavailable") || "Outside Working Hours"}
              </span>
            </div>
           
            <div className="legend-note">
              <span>
                {t("stepThreeAvailability.monthNote") ||
                  "Note: Past dates and days outside your working hours are automatically unavailable. Selections are saved per month."}
              </span>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="buttons">
          <button
            className="Cancel"
            onClick={() =>
              isEdit ? handleNavigateWithEdit(2) : handleNavigateWithParams(2)
            }
          >
            {t("stepThreeAvailability.previous") || "Previous"}
          </button>
          <button
            className="Next"
            onClick={() => {
              handleSave(isEdit);

              if (!isEdit) {
                handleNavigateWithParams(4);
              }
            }}
          >
            {isEdit
              ? status === "loading"
                ? t("stepThreeAvailability.loading") || "Loading..."
                : t("stepThreeAvailability.save") || "Save"
              : t("stepThreeAvailability.next") || "Next"}
          </button>
        </div>
      </div>
      {status === "Success" && (
        <div className={"message_Venue"}>
          <div>
            {status === "Success" && <Success />}
            {t("stepTwo.changesSaved")}
          </div>
        </div>
      )}
      {status === "error" && errorMessage && (
        <div className={"message_Venue error_message"}>
          <div>
            <svg width="104" height="104" viewBox="0 0 104 104" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="52" cy="52" r="52" fill="#FF4444"/>
              <path d="M52 32V60M52 72H52.04" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {errorMessage}
          </div>
        </div>
      )}
    </div>
  );
};

export default StepThreeAvailability;
